// ST-1281 -Claude- Location input for city/address/PIN plus explicit GPS.
// GPS permission is requested only after the user clicks Current location.

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, type NearrmeLocation } from '../hooks/useLocation';

export interface LocationInputProps {
  onLocationResolved?: (loc: NearrmeLocation) => void;
  compact?: boolean;
}

export function LocationInput({ onLocationResolved, compact = false }: LocationInputProps) {
  const { t } = useTranslation();
  const { location, loading, error, requestGeolocation, lookupByText, clearLocation } = useLocation();
  const [query, setQuery] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const loc = await lookupByText(query);
    if (loc) {
      setQuery('');
      onLocationResolved?.(loc);
    }
  };

  const handleGps = () => {
    requestGeolocation();
  };

  if (location && compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300 text-sm">
          <span className="max-w-[300px] truncate" title={location.displayName}>
            {location.displayName}
          </span>
          <button
            type="button"
            onClick={clearLocation}
            className="text-xs font-semibold hover:text-orange-900 dark:hover:text-orange-100"
            aria-label="Clear location"
          >
            Clear
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="flex items-center gap-2 flex-wrap justify-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={location ? location.displayName : 'Enter city, address, or PIN code'}
            className="w-full h-10 px-4 pr-14 rounded-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            disabled={loading}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              aria-label="Clear input"
            >
              Clear
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || query.length < 2}
          className="h-10 px-4 rounded-full bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Finding...' : 'Find'}
        </button>
        <button
          type="button"
          onClick={handleGps}
          disabled={loading}
          className="h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          title={t('landing.useMyLocation')}
        >
          Current location
        </button>
      </form>
      {error && (
        <div className="mt-2 text-center text-sm text-red-500">{error}</div>
      )}
      {location && (
        <div className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
          <span className="truncate inline-block max-w-[400px] align-bottom" title={location.displayName}>
            {location.displayName}
          </span>
          <button
            type="button"
            onClick={clearLocation}
            className="ml-2 underline hover:text-orange-500"
          >
            change
          </button>
        </div>
      )}
    </div>
  );
}
